import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { DEFAULT_TITLE, applyDocumentTitle } from "@lib/documentTitle";
import {
    DocumentTitleContext,
    DocumentTitleContextValue,
} from "@hooks/useDocumentTitleOverrides";

interface PathTitle {
    pattern: RegExp;
    title: string;
}

/**
 * Path patterns in order of specificity (more specific first).
 * First matching pattern wins. Use :param for dynamic segments.
 */
const PATH_TITLES: PathTitle[] = [
    // Public
    { pattern: /^\/register(?:\/|$)/, title: "Sign up" },
    // Private
    { pattern: /^\/transactions(?:\/|$)/, title: "Transactions" },
    { pattern: /^\/rules(?:\/|$)/, title: "Rules" },
    { pattern: /^\/categories(?:\/|$)/, title: "Transaction categories" },
    { pattern: /^\/classes(?:\/|$)/, title: "Classes" },
    { pattern: /^\/counterparties(?:\/|$)/, title: "Counterparties" },
    {
        pattern: /^\/document-categories(?:\/|$)/,
        title: "Document categories",
    },
    { pattern: /^\/activity(?:\/|$)/, title: "Activity" },
    { pattern: /^\/history(?:\/|$)/, title: "Activity" },
    { pattern: /^\/invoicing(?:\/|$)/, title: "Invoicing" },
    { pattern: /^\/billing(?:\/|$)/, title: "Bill pay" },
    { pattern: /^\/documents(?:\/|$)/, title: "Documents" },
    { pattern: /^\/insights(?:\/|$)/, title: "Insights" },
    { pattern: /^\/cashflow(?:\/|$)/, title: "Insights" },
    { pattern: /^\/accounts(?:\/|$)/, title: "Accounts" },
    { pattern: /^\/chart-of-accounts(?:\/|$)/, title: "Chart of accounts" },
    { pattern: /^\/reconciliation(?:\/|$)/, title: "Reconciliation" },
    { pattern: /^\/accounting\/profit-loss(?:\/|$)/, title: "Profit and loss" },
    { pattern: /^\/accounting\/balance-sheet(?:\/|$)/, title: "Balance sheet" },
    {
        pattern: /^\/accounting\/general-ledger(?:\/|$)/,
        title: "General ledger",
    },
    { pattern: /^\/accounting\/trial-balance(?:\/|$)/, title: "Trial balance" },
    {
        pattern: /^\/accounting\/intercompany-transactions(?:\/|$)/,
        title: "Intercompany",
    },
    {
        pattern: /^\/accounting\/elimination-exceptions(?:\/|$)/,
        title: "Intercompany",
    },
    {
        pattern: /^\/accounting\/elimination-detail(?:\/|$)/,
        title: "Intercompany",
    },
    { pattern: /^\/accounting(?:\/|$)/, title: "Accounting" },
    { pattern: /^\/settings(?:\/|$)/, title: "Settings" },
    { pattern: /^\/add-account(?:\/|$)/, title: "Add account" },
    { pattern: /^\/new-entity(?:\/|$)/, title: "New entity" },
    { pattern: /^\/onboarding(?:\/|$)/, title: "Onboarding" },
];

function getTitleForPathname(
    pathname: string,
    overrides: Map<string, PathTitle[]>,
): string {
    const normalized = pathname.replace(/\/$/, "") || "/";

    // Check overrides first (route-group-specific titles take priority)
    for (const pathTitles of overrides.values()) {
        for (const { pattern, title } of pathTitles) {
            if (pattern.test(normalized)) {
                return title;
            }
        }
    }

    for (const { pattern, title } of PATH_TITLES) {
        if (pattern.test(normalized)) {
            return title;
        }
    }
    if (normalized === "/") {
        return "Actions";
    }
    return DEFAULT_TITLE;
}

/**
 * Provider that manages document title overrides from route groups.
 * Wrap this around the route tree so that route groups can push their
 * own title mappings via useDocumentTitleOverrides.
 */
export const DocumentTitleProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const overridesRef = useRef(new Map<string, PathTitle[]>());
    const [version, setVersion] = useState(0);

    const contextValue = useRef<DocumentTitleContextValue>({
        registerOverrides: (id, pathTitles) => {
            overridesRef.current.set(id, pathTitles);
            setVersion((v) => v + 1);
        },
        unregisterOverrides: (id) => {
            overridesRef.current.delete(id);
            setVersion((v) => v + 1);
        },
    }).current;

    return (
        <DocumentTitleContext.Provider value={contextValue}>
            {children}
            <DocumentTitleUpdater
                overrides={overridesRef.current}
                version={version}
            />
        </DocumentTitleContext.Provider>
    );
};

/**
 * Internal component that reads the current pathname and all registered
 * overrides, then sets document.title. Rendered inside the provider so
 * it always has the latest overrides — no render-order dependency.
 */
const DocumentTitleUpdater: React.FC<{
    overrides: Map<string, PathTitle[]>;
    version: number;
}> = ({ overrides, version }) => {
    const { pathname } = useLocation();

    useEffect(() => {
        const title = getTitleForPathname(pathname, overrides);
        applyDocumentTitle(title);
    }, [pathname, overrides, version]);

    return null;
};

/**
 * Sets document.title based on the current route pathname (without
 * override support). Used by PublicInterface where route-group overrides
 * are not needed. For private routes, prefer <DocumentTitleProvider>.
 */
export const DocumentTitle: React.FC = () => {
    const { pathname } = useLocation();

    useEffect(() => {
        const title = getTitleForPathname(pathname, new Map());
        applyDocumentTitle(title);
    }, [pathname]);

    return null;
};
